// ============================================
// B356 Fase 1 (AI_154) — PÁGINA "NUMERAÇÃO SNCR" (médico-only)
// ============================================
// Onde o médico obtém, com o login gov.br dele, os blocos de numeração de
// Receita de Controle Especial (RCE) e de Receita sujeita à retenção (RET)
// — antimicrobiano, GLP-1 — que o app consome ao ASSINAR cada documento
// (RDC 1.000/2025, Art. 7º).
//
// Precisa rodar em hepato.app.br: o login gov.br do SNCR só devolve o usuário
// a domínios .br. Em hepato.app, a página mostra o atalho para o endereço .br.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../auth/context/AuthContext";
import Login from "../auth/components/LoginNovo";
import { ehMedico } from "../../shared/utils/permissoesCategoria";
import { theme } from "../../shared/styles/theme";
import {
  ambienteSncr,
  cnpjPlataforma,
  cnpjValido,
  definirModoTeste,
  ENDERECO_PAGINA_SNCR,
  ESTADO_RETORNO_SNCR,
  numeracaoNasAssinaturas,
  LIMITES_RCE_RET,
  origemAceitaPeloSncr,
} from "./configSncr";
import {
  DadosPrescritorSncr,
  prescritorDoCadastro,
  solicitarBlocoRceRet,
  trocarSessaoPorToken,
  urlLoginSncr,
} from "./sncrCliente";
import { EstadoCofre, lerCofre, registrarBloco } from "./cofreNumeracao";
import { abrirObtencaoPeloApp } from "./ticketSncr";
import { BlocoNumeracao, TipoNumeracao } from "./numeracaoSncr";

const CHAVE_PEDIDO = "sncr_pedido_tipos";
const NOME_TIPO: Record<TipoNumeracao, string> = {
  RCE: "Receita de Controle Especial (RCE)",
  RET: "Receita sujeita à retenção (RET) — antimicrobiano, GLP-1",
};

type Mensagem = { tipo: "ok" | "erro" | "info"; texto: string };

const CHAVE_PENDENTES = "sncr_blocos_pendentes";

interface PedidoSalvo {
  tipos: TipoNumeracao[];
  prescritor: DadosPrescritorSncr;
  /** Fluxo logado em hepato.app.br: blocos ficam pendentes até o login. */
  uid?: string;
  /** Fluxo vindo de hepato.app: blocos são entregues ao servidor (sncr-ticket). */
  ticket?: string;
  cnpj?: string;
}
interface BlocoPendente {
  uid: string;
  tipo: TipoNumeracao;
  bloco: BlocoNumeracao;
}

/** Lê e APAGA o pedido feito antes de ir ao gov.br (uso único). */
function lerPedido(): PedidoSalvo | null {
  try {
    const bruto = JSON.parse(sessionStorage.getItem(CHAVE_PEDIDO) || "null");
    sessionStorage.removeItem(CHAVE_PEDIDO);
    if (!bruto || (!bruto.uid && !bruto.ticket) || !bruto.prescritor?.documento) return null;
    const tipos = (Array.isArray(bruto.tipos) ? bruto.tipos : []).filter(
      (t: unknown) => t === "RCE" || t === "RET"
    );
    return tipos.length
      ? { tipos, prescritor: bruto.prescritor, uid: bruto.uid, ticket: bruto.ticket, cnpj: bruto.cnpj }
      : null;
  } catch {
    return null;
  }
}

/** Há pedido do fluxo por ticket nesta aba? (sem consumir) */
function pedidoEhTicket(): boolean {
  try {
    return !!JSON.parse(sessionStorage.getItem(CHAVE_PEDIDO) || "null")?.ticket;
  } catch {
    return false;
  }
}

type FluxoTicket = "redirecionando" | "processando" | "concluido" | "erro";

function lerPendentes(): BlocoPendente[] {
  try {
    const v = JSON.parse(sessionStorage.getItem(CHAVE_PENDENTES) || "[]");
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}

function gravarPendentes(lista: BlocoPendente[]): void {
  try {
    if (lista.length) sessionStorage.setItem(CHAVE_PENDENTES, JSON.stringify(lista));
    else sessionStorage.removeItem(CHAVE_PENDENTES);
  } catch {
    /* nada a fazer */
  }
}

function adicionarPendente(p: BlocoPendente): void {
  gravarPendentes([...lerPendentes(), p]);
}

export default function PaginaSncr() {
  const { usuarioLogado, loading } = useAuth();
  const amb = ambienteSncr();
  const [cofres, setCofres] = useState<Partial<Record<TipoNumeracao, EstadoCofre>>>({});
  const [selecionados, setSelecionados] = useState<TipoNumeracao[]>(["RCE", "RET"]);
  const [mensagens, setMensagens] = useState<Mensagem[]>([]);
  const [processando, setProcessando] = useState(false);
  const callbackTratado = useRef(false);
  // Fluxo por TICKET (vindo de hepato.app): esta aba não exige login do app.
  const [fluxoTicket, setFluxoTicket] = useState<FluxoTicket | null>(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get("ticket")) return "redirecionando";
    if ((q.get("session_id") || q.get("error")) && pedidoEhTicket()) return "processando";
    return null;
  });
  const uid = usuarioLogado?.id;
  const [modoTeste, setModoTeste] = useState(false);
  useEffect(() => setModoTeste(numeracaoNasAssinaturas(uid)), [uid]);
  const prescritor = prescritorDoCadastro(usuarioLogado?.registroProfissional);
  const cnpj = cnpjPlataforma();
  const naOrigemCerta =
    typeof window !== "undefined" && origemAceitaPeloSncr(window.location.hostname);

  // Só a leitura MAIS RECENTE vale: validação ao vivo (AI_154) — a leitura feita
  // ao entrar (cofre ainda vazio) chegava depois da feita após guardar o bloco e
  // a tela mostrava "0 de 0" com o bloco já no cofre.
  const leituraAtual = useRef(0);
  const recarregar = useCallback(async () => {
    if (!uid) return;
    const minha = ++leituraAtual.current;
    // Cada tipo isolado: a falha de um não deixa o outro em "Carregando…".
    const novos: Partial<Record<TipoNumeracao, EstadoCofre>> = {};
    for (const t of ["RCE", "RET"] as TipoNumeracao[]) {
      try {
        novos[t] = await lerCofre(uid, amb, t);
      } catch (e) {
        console.error(`Erro ao ler o cofre ${t}:`, e);
      }
    }
    if (minha === leituraAtual.current) setCofres(novos);
  }, [uid, amb]);

  // Chegada com ?ticket= (aberta pelo hepato.app): busca o pedido no servidor e
  // vai direto ao gov.br — sem login do app neste endereço.
  useEffect(() => {
    const ticket = new URLSearchParams(window.location.search).get("ticket");
    if (!ticket) return;
    window.history.replaceState({}, document.title, window.location.pathname);
    (async () => {
      try {
        const resp = await fetch(`/api/sncr-ticket?ticket=${encodeURIComponent(ticket)}`);
        const r = await resp.json().catch(() => ({}));
        if (!resp.ok) throw new Error(r?.error || `HTTP ${resp.status}`);
        sessionStorage.setItem(
          CHAVE_PEDIDO,
          JSON.stringify({ tipos: r.tipos, prescritor: r.prescritor, ticket, cnpj: r.cnpj })
        );
        window.location.href = urlLoginSncr(window.location.origin, amb, ESTADO_RETORNO_SNCR);
      } catch (e: any) {
        setFluxoTicket("erro");
        setMensagens([{ tipo: "erro", texto: e?.message || "Pedido inválido. Refaça em hepato.app." }]);
      }
    })();
  }, [amb]);

  // ------------------------------------------------------------------
  // Volta do gov.br (?session_id=…). Validação ao vivo (AI_154): o app faz
  // LOGOUT ao sair da página (proteção de segurança) — ir ao gov.br conta como
  // sair, e na volta o médico está deslogado. O session_id vive 30 s e não pode
  // esperar o novo login. Por isso a troca session_id → token → blocos acontece
  // JÁ, SEM depender do login do app (só usa o CRM guardado antes de sair), e os
  // blocos recebidos ficam PENDENTES nesta aba (sessionStorage) até o médico
  // entrar — então vão para o cofre. A proteção de logout não foi afrouxada.
  // ------------------------------------------------------------------
  const [versaoPendentes, setVersaoPendentes] = useState(0);

  useEffect(() => {
    if (callbackTratado.current) return;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const erroGov = params.get("error");
    if (!sessionId && !erroGov) return;
    callbackTratado.current = true;
    window.history.replaceState({}, document.title, window.location.pathname);
    if (erroGov) {
      setMensagens([{ tipo: "erro", texto: `O login gov.br não foi concluído (${erroGov}).` }]);
      setFluxoTicket((f) => (f ? "erro" : f));
      return;
    }
    const pedido = lerPedido();
    if (!pedido) {
      setMensagens([
        {
          tipo: "erro",
          texto:
            "Não encontrei o pedido feito antes do login gov.br (outra aba ou navegador?). Refaça o pedido nesta mesma aba.",
        },
      ]);
      setFluxoTicket((f) => (f ? "erro" : f));
      return;
    }
    (async () => {
      setProcessando(true);
      const novas: Mensagem[] = [];
      let diag = "";
      try {
        // Diagnóstico (sem expor o código): segundos desde a volta do gov.br e
        // formato do session_id — o código vale 30 s e é de uso único.
        diag = `${Math.round(performance.now() / 1000)} s após a volta; código com ${sessionId!.length} caracteres`;
        // Troca pelo SERVIDOR (api/sncr-sessao): pelo navegador a Anvisa devolveu
        // sempre 401. O token fica no servidor; voltam só os blocos.
        const resp = await fetch("/api/sncr-sessao", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            sessionId,
            ambiente: amb,
            tipos: pedido.tipos,
            prescritor: pedido.prescritor,
            cnpj: pedido.cnpj || cnpjPlataforma(),
          }),
        });
        let r: any = await resp.json().catch(() => ({}));
        const resumo = Array.isArray(r?.tentativas)
          ? r.tentativas.map((t: any) => `${t.variante}:${t.status}`).join(", ")
          : `HTTP ${resp.status}`;
        diag += `; servidor: ${resumo}`;
        if (r?.firewall) {
          // Firewall barrou o servidor antes da API (código intacto): a troca e
          // o pedido dos blocos saem DESTE navegador, que o firewall aceita.
          diag += "; navegador: trocando";
          const token = await trocarSessaoPorToken(sessionId!, amb);
          const blocos: any[] = [];
          for (const tipo of pedido.tipos) {
            try {
              const b = await solicitarBlocoRceRet(
                token,
                tipo,
                pedido.prescritor,
                pedido.cnpj || cnpjPlataforma(),
                amb
              );
              blocos.push({ tipo, ...b });
            } catch (e: any) {
              blocos.push({ tipo, erro: e?.message || "falha ao pedir o bloco." });
            }
          }
          r = { variante: "navegador", blocos };
        } else if (!resp.ok) {
          throw new Error(r?.error || `HTTP ${resp.status}`);
        }
        if (pedido.ticket) {
          // Fluxo por ticket: o SERVIDOR grava no cofre do dono do ticket.
          const bons = (r.blocos || []).filter((b: any) => !b.erro && b.inicio);
          for (const b of (r.blocos || []).filter((b: any) => b.erro || !b.inicio)) {
            novas.push({ tipo: "erro", texto: `${b.tipo}: ${b.erro || "resposta sem bloco."}` });
          }
          if (bons.length) {
            const e = await fetch("/api/sncr-ticket", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ acao: "entregar", ticket: pedido.ticket, blocos: bons }),
            });
            const er = await e.json().catch(() => ({}));
            if (!e.ok) {
              for (const b of bons) {
                novas.push({
                  tipo: "erro",
                  texto: `${b.tipo}: o SNCR entregou ${b.inicio} a ${b.fim}, mas não foi possível guardar (${er?.error || `HTTP ${e.status}`}). ANOTE esta faixa e não peça outro bloco antes de resolver.`,
                });
              }
            } else {
              for (const g of er.gravados || []) {
                novas.push({
                  tipo: "ok",
                  texto: `${g.tipo}: bloco de ${g.quantidade} números guardado na sua conta (${g.inicio} a ${g.fim}).`,
                });
              }
              for (const x of er.recusados || []) {
                novas.push({ tipo: "erro", texto: `${x.tipo}: bloco recusado (${x.erro}).` });
              }
            }
          }
          r.blocos = [];
        }
        for (const b of r.blocos || []) {
          if (b.erro || !b.inicio) {
            novas.push({ tipo: "erro", texto: `${b.tipo}: ${b.erro || "resposta sem bloco."}` });
            continue;
          }
          adicionarPendente({
            uid: pedido.uid || "",
            tipo: b.tipo,
            bloco: {
              inicio: b.inicio,
              fim: b.fim,
              quantidade: b.quantidade,
              obtidoEm: new Date().toISOString(),
            },
          });
          novas.push({
            tipo: "info",
            texto: `${b.tipo}: o SNCR entregou ${b.quantidade} números (${b.inicio} a ${b.fim}) — acesso aceito via "${r.variante}".`,
          });
        }
      } catch (e: any) {
        novas.push({
          tipo: "erro",
          texto: `Não foi possível concluir a autenticação no SNCR: ${e?.message || "erro desconhecido"}. Tente de novo. (Diagnóstico: ${diag || "falha antes da chamada"}.) Nenhum número foi gasto.`,
        });
      }
      setMensagens(novas);
      setProcessando(false);
      if (pedido.ticket) setFluxoTicket(novas.some((m) => m.tipo === "ok") ? "concluido" : "erro");
      setVersaoPendentes((v) => v + 1);
    })();
  }, [amb]);

  // Com o médico logado: guarda no cofre os blocos pendentes DELE e lê o saldo.
  useEffect(() => {
    if (!uid) return;
    const pendentes = lerPendentes();
    const meus = pendentes.filter((p) => p.uid === uid);
    if (meus.length === 0) {
      recarregar();
      return;
    }
    (async () => {
      const novas: Mensagem[] = [];
      const restantes = pendentes.filter((p) => p.uid !== uid);
      for (const p of meus) {
        try {
          try {
            await registrarBloco(uid, amb, p.tipo, p.bloco);
          } catch {
            await registrarBloco(uid, amb, p.tipo, p.bloco);
          }
          novas.push({
            tipo: "ok",
            texto: `${p.tipo}: bloco de ${p.bloco.quantidade} números guardado (${p.bloco.inicio} a ${p.bloco.fim}).`,
          });
        } catch (e: any) {
          restantes.push(p);
          novas.push({
            tipo: "erro",
            texto: `${p.tipo}: o SNCR entregou ${p.bloco.inicio} a ${p.bloco.fim}, mas não foi possível guardá-lo (${e?.message || "erro"}). ANOTE esta faixa e não peça outro bloco antes de resolver.`,
          });
        }
      }
      gravarPendentes(restantes);
      setMensagens((m) => [...m.filter((x) => x.tipo !== "info"), ...novas]);
      recarregar();
    })();
  }, [uid, versaoPendentes]); // eslint-disable-line

  // Volta de outra aba (ex.: a do gov.br): relê o saldo.
  useEffect(() => {
    const ler = () => recarregar();
    window.addEventListener("focus", ler);
    return () => window.removeEventListener("focus", ler);
  }, [recarregar]);

  const iniciarLogin = () => {
    if (!selecionados.length) return;
    if (!naOrigemCerta) {
      // Em hepato.app: aba do gov.br via ticket, sem login em hepato.app.br.
      abrirObtencaoPeloApp(selecionados);
      return;
    }
    const ok = window.confirm(
      `Você vai entrar com o gov.br para pedir ${selecionados.length === 2 ? "dois blocos" : "um bloco"} de ${LIMITES_RCE_RET.numerosPorPedido} números.\n\n` +
        `A Anvisa permite ${LIMITES_RCE_RET.pedidosPorMes} pedidos por mês. Peça só quando o saldo estiver acabando.\n\nContinuar?`
    );
    if (!ok) return;
    if (!prescritor || !uid) return;
    try {
      sessionStorage.setItem(
        CHAVE_PEDIDO,
        JSON.stringify({ tipos: selecionados, prescritor, uid })
      );
    } catch {
      alert("Este navegador bloqueou o armazenamento da sessão; não é possível continuar.");
      return;
    }
    // client_url = só a ORIGEM (ver destinoRetornoSncr em configSncr.ts); a
    // volta cai na raiz com state=sncr e é encaminhada para esta página.
    window.location.href = urlLoginSncr(window.location.origin, amb, ESTADO_RETORNO_SNCR);
  };

  // Homologação: interruptor do "modo teste" (numeração de TESTE nas assinaturas,
  // com tarja "sem valor legal"). Fica no navegador DESTE endereço — por isso
  // aparece nos dois (hepato.app e hepato.app.br): ligue onde você assina.
  const blocoModoTeste =
    amb === "hmg" ? (
      <label
        style={{
          display: "flex",
          gap: 8,
          alignItems: "flex-start",
          fontSize: 12,
          background: "#fef3c7",
          border: "1px solid #f59e0b",
          borderRadius: 8,
          padding: "8px 10px",
          margin: "12px 0",
        }}
      >
        <input
          type="checkbox"
          checked={modoTeste}
          onChange={(e) => {
            definirModoTeste(e.target.checked, uid);
            setModoTeste(e.target.checked);
          }}
        />
        <span>
          <strong>Modo teste (homologação)</strong> — só para você, neste navegador ({window.location.host}), as receitas
          ASSINADAS digitalmente passam a consumir a numeração de teste e saem com a tarja
          "HOMOLOGAÇÃO — SEM VALOR LEGAL". Use só com paciente de teste; desligue depois.
        </span>
      </label>
    ) : null;

  // ---------------- renderização ----------------
  if (fluxoTicket) {
    return (
      <Moldura>
        {fluxoTicket === "redirecionando" && <p>Abrindo o gov.br…</p>}
        {fluxoTicket === "processando" && mensagens.length === 0 && (
          <p>Obtendo a numeração no SNCR…</p>
        )}
        {mensagens.map((m, i) => (
          <p
            key={i}
            style={{
              fontSize: 14,
              color: m.tipo === "erro" ? "#991b1b" : m.tipo === "ok" ? "#166534" : theme.gray[700],
            }}
          >
            {m.tipo === "ok" ? "✅ " : m.tipo === "erro" ? "❌ " : ""}
            {m.texto}
          </p>
        ))}
        {(fluxoTicket === "concluido" || fluxoTicket === "erro") && (
          <>
            <p style={{ fontSize: 14 }}>
              {fluxoTicket === "concluido"
                ? "Pronto. Pode fechar esta aba e voltar ao hepato.app — o saldo já aparece lá."
                : "Feche esta aba e tente de novo pelo hepato.app."}
            </p>
            <button type="button" onClick={() => window.close()} style={botao(true)}>
              Fechar esta aba
            </button>
          </>
        )}
      </Moldura>
    );
  }
  if (loading) return <Moldura><p>Carregando…</p></Moldura>;
  if (!usuarioLogado) return <Login />;
  if (!ehMedico(usuarioLogado)) {
    return (
      <Moldura>
        <p>A numeração do SNCR é pedida pelo próprio médico prescritor.</p>
        <a href="/">Voltar ao prontuário</a>
      </Moldura>
    );
  }
  const problemas: string[] = [];
  if (!prescritor)
    problemas.push("Seu cadastro não tem CRM e UF — complete o registro profissional no seu perfil.");
  if (!cnpjValido(cnpj))
    problemas.push("CNPJ da plataforma não configurado (REACT_APP_SNCR_CNPJ na Vercel).");

  return (
    <Moldura>
      <div
        style={{
          display: "inline-block",
          fontSize: 11,
          fontWeight: 700,
          padding: "2px 8px",
          borderRadius: 8,
          background: amb === "prd" ? "#dcfce7" : "#fef3c7",
          color: amb === "prd" ? "#166534" : "#92400e",
          marginBottom: 12,
        }}
      >
        {amb === "prd" ? "PRODUÇÃO — numeração real" : "HOMOLOGAÇÃO — números de teste, sem valor legal"}
      </div>

      {prescritor && (
        <p style={{ margin: "0 0 12px", fontSize: 13, color: theme.gray[600] }}>
          Prescritor: {usuarioLogado.nome} — {prescritor.conselho} {prescritor.documento}/{prescritor.uf}
        </p>
      )}

      {(["RCE", "RET"] as TipoNumeracao[]).map((t) => {
        const c = cofres[t];
        return (
          <div
            key={t}
            style={{
              border: `1px solid ${theme.gray[200]}`,
              borderRadius: 8,
              padding: "10px 12px",
              marginBottom: 8,
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <input
              type="checkbox"
              checked={selecionados.includes(t)}
              onChange={(e) =>
                setSelecionados((s) =>
                  e.target.checked ? Array.from(new Set([...s, t])) : s.filter((x) => x !== t)
                )
              }
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{NOME_TIPO[t]}</div>
              <div style={{ fontSize: 12, color: theme.gray[600] }}>
                {c
                  ? `Saldo: ${c.saldo} de ${c.total} números${c.total ? ` (${c.usados} usados)` : ""}`
                  : "Carregando saldo…"}
              </div>
            </div>
          </div>
        );
      })}

      {problemas.map((p) => (
        <p key={p} style={{ color: "#991b1b", fontSize: 13 }}>
          ⚠️ {p}
        </p>
      ))}

      <button
        type="button"
        disabled={processando || problemas.length > 0 || selecionados.length === 0}
        onClick={iniciarLogin}
        style={botao(!(processando || problemas.length > 0 || selecionados.length === 0))}
      >
        {processando ? "Obtendo numeração…" : "Obter numeração com gov.br"}
      </button>

      <p style={{ fontSize: 12, color: theme.gray[500] }}>
        Cada pedido traz {LIMITES_RCE_RET.numerosPorPedido} números; a Anvisa permite{" "}
        {LIMITES_RCE_RET.pedidosPorMes} pedidos por mês. O app usa um número a cada receita assinada
        digitalmente. Receita impressa e assinada à caneta não usa numeração.
      </p>

      {mensagens.map((m, i) => (
        <p
          key={i}
          style={{
            fontSize: 13,
            color: m.tipo === "erro" ? "#991b1b" : m.tipo === "ok" ? "#166534" : theme.gray[700],
          }}
        >
          {m.tipo === "ok" ? "✅ " : m.tipo === "erro" ? "❌ " : ""}
          {m.texto}
        </p>
      ))}

      {blocoModoTeste}

      <a href="/" style={{ fontSize: 13 }}>
        ← Voltar ao prontuário
      </a>
    </Moldura>
  );
}

function Moldura({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: theme.gray[50],
        display: "flex",
        justifyContent: "center",
        padding: "32px 16px",
        fontFamily: theme.fonts.sans,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 560,
          background: "white",
          borderRadius: 12,
          boxShadow: theme.shadows.md,
          padding: 24,
          alignSelf: "flex-start",
        }}
      >
        <h1 style={{ fontSize: 18, margin: "0 0 12px" }}>Numeração SNCR (Anvisa)</h1>
        {children}
      </div>
    </div>
  );
}

function botao(ativo: boolean): React.CSSProperties {
  return {
    display: "inline-block",
    padding: "10px 16px",
    borderRadius: 8,
    border: "none",
    background: ativo ? "#1e40af" : theme.gray[300],
    color: "white",
    fontWeight: 600,
    fontSize: 14,
    cursor: ativo ? "pointer" : "not-allowed",
    textDecoration: "none",
    margin: "8px 0",
  };
}
